import tailwindConfig from '@/tailwind.config'
import { aperture, pipe, last, gte, lte, keys } from 'ramda'
import type { Ref } from 'vue'

export type Breakpoint = 'all' | 'sm' | 'md' | 'lg' | 'xl' | 'xl1' | 'xl2' | 'xl3'
export const allBreakpoints: Breakpoint[] = ['all', 'sm', 'md', 'lg', 'xl', 'xl1', 'xl2', 'xl3']

export function useMediaQuery(
  container: Ref<HTMLElement | null> = ref(document.body),
  sizes: { [key: string]: string } = tailwindConfig.theme.screens
) {
  const breakpoint: Ref<Breakpoint> = ref('all')
  const maxWidth: string = _getMaxWidth(sizes)
  const breakpoints: Breakpoint[][] = <Breakpoint[][]>_getBreakpointsFrom(sizes)

  const observer = new ResizeObserver(([entry]) => {
    onResize(entry.contentRect.width)
  })

  watch(
    container,
    container => {
      if (!container) return

      observer.observe(container)
      onResize(container.getBoundingClientRect().width)
    },
    { immediate: true }
  )

  onUnmounted(() => {
    observer.disconnect()
  })

  function onResize(width: number): void {
    breakpoint.value = _getBreakpoint(breakpoints, width / parseFloat(getComputedStyle(document.documentElement).fontSize))
  }

  function isMobileBreakpoint(): boolean {
    return breakpoint.value === 'all' || breakpoint.value === 'sm'
  }

  function _getBreakpoint(breakpoints: Breakpoint[][], width: number): Breakpoint {
    for (let points of breakpoints) {
      const [w1, w2] = _parseSizes(sizes, <Breakpoint[]>points)

      if (_isBetween(w1, w2, width)) return _getPoint(<Breakpoint[]>points, w2, width)

      if (_exceedsMaxWidth(sizes, width)) return <Breakpoint>maxWidth
    }

    return 'all'
  }

  function _getMaxWidth(sizes: { [key: string]: string }): string {
    return pipe(keys, last)(sizes) as string
  }

  function _getBreakpointsFrom(sizes: { [key: string]: string }): string[][] {
    return pipe(keys, aperture(2))(sizes) as string[][]
  }

  function _parseSizes(sizes: { [key: string]: string }, breakpoints: Breakpoint[]): number[] {
    return [parseInt(sizes[breakpoints[0]]), parseInt(sizes[breakpoints[1]])]
  }

  function _isBetween(w1: number, w2: number, width: number): boolean {
    return gte(width, w1) && lte(width, w2)
  }

  function _getPoint(breakpoints: Breakpoint[], w2: number, width: number): Breakpoint {
    return width === w2 ? breakpoints[1] : breakpoints[0]
  }

  function _exceedsMaxWidth(sizes: { [key: string]: string }, width: number) {
    return gte(width, parseInt(sizes[maxWidth]))
  }

  return { breakpoint: readonly(breakpoint), isMobileBreakpoint }
}
